import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/**
 * Vara rasterises handwriting into fixed pixel paths with no viewBox, so the
 * result cannot be scaled with CSS after the fact — a hard-coded `fontSize`
 * stays the same width on a 320px phone and a 27-inch display.
 *
 * This wrapper takes its size from CSS instead: it reads its own computed
 * font-size, so the same fluid type scale that drives the surrounding headings
 * drives the handwriting. After the first draw it measures the rendered glyphs
 * to learn the word's width-per-font-size, then keeps the word inside its
 * container at every width, redrawing (without animation) whenever the target
 * size moves by a pixel or more.
 */
export default function HandwrittenText({
  id,
  text,
  color,
  strokeWidth = 1.2,
  textAlign = "left",
  duration = 1200,
  delay = 300,
  className = "",
  /** Share of the container width the word is allowed to occupy. */
  fill = 0.98,
}) {
  const wrapperRef = useRef(null);
  const targetRef = useRef(null);
  const widthPerUnitRef = useRef(null);
  const drawnSizeRef = useRef(0);
  const inView = useInView(wrapperRef, { once: true });
  const [layout, setLayout] = useState({ fontSize: 0, containerWidth: 0 });

  /* Track the CSS-driven font size and the container width together: either one
     changing can change the size the word should be drawn at. */
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof window === "undefined") return undefined;

    const measure = () => {
      const fontSize = parseFloat(window.getComputedStyle(node).fontSize);
      const containerWidth = node.clientWidth;
      if (!Number.isFinite(fontSize) || !containerWidth) return;
      setLayout((current) =>
        current.fontSize === fontSize && current.containerWidth === containerWidth
          ? current
          : { fontSize, containerWidth }
      );
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const { fontSize, containerWidth } = layout;
    if (!inView || !targetRef.current || !fontSize || !containerWidth) return undefined;

    /* Once the word's width-per-font-size is known, cap the size so it always
       fits. Before that, the first draw doubles as the measurement. */
    const widthPerUnit = widthPerUnitRef.current;
    const fitted = widthPerUnit
      ? Math.min(fontSize, (containerWidth * fill) / widthPerUnit)
      : fontSize;
    const nextSize = Math.max(1, Math.round(fitted));

    if (Math.abs(nextSize - drawnSizeRef.current) < 1) return undefined;

    const isRedraw = drawnSizeRef.current > 0;
    drawnSizeRef.current = nextSize;

    const draw = async () => {
      const { default: Vara } = await import("vara");
      if (cancelled || !targetRef.current) return;

      targetRef.current.innerHTML = "";
      const instance = new Vara(
        `#${id}`,
        "/fonts/shadows-into-light.json",
        [
          {
            text,
            fontSize: nextSize,
            strokeWidth,
            color,
            textAlign,
            duration: isRedraw ? 0 : duration,
            delay: isRedraw ? 0 : delay,
          },
        ]
      );

      instance.ready(() => {
        if (cancelled || !targetRef.current || widthPerUnitRef.current) return;
        const group = targetRef.current.querySelector("svg g");
        if (!group || typeof group.getBBox !== "function") return;

        const drawnWidth = group.getBBox().width;
        if (!drawnWidth) return;

        widthPerUnitRef.current = drawnWidth / nextSize;
        /* Re-run the sizing effect now that the ratio is known. */
        setLayout((current) => ({ ...current }));
      });
    };

    draw();

    return () => {
      cancelled = true;
    };
  }, [inView, layout, id, text, color, strokeWidth, textAlign, duration, delay, fill]);

  useEffect(() => {
    const targetNode = targetRef.current;
    return () => {
      if (targetNode) targetNode.innerHTML = "";
    };
  }, []);

  return (
    <div ref={wrapperRef} className={className}>
      <div id={id} ref={targetRef} data-handwriting="" />
    </div>
  );
}
